import { styled, keyframes } from '@mui/material/styles';
import {
  AppBar as MuiAppBar,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Menu,
  MenuItem,
  Modal,
  Select,
  TextField,
  Toolbar,
  Typography,
  FormControl,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import { HEADER_HEIGHT, DRAWER_WIDTH } from '../../constants/constants';

const drawerWidth = DRAWER_WIDTH;

// ============================================
// Animations
// ============================================
export const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(15, 127, 133, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(15, 127, 133, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(15, 127, 133, 0);
  }
`;

// ============================================
// AppBar & Toolbar
// ============================================
export const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  paddingLeft: '32px',
  paddingRight: '32px',
  boxShadow: 'none',
  borderBottom: '1px solid #f2f2f2',
  height: `${HEADER_HEIGHT}px`,
  backgroundColor: '#fff',
  display: 'flex',
  justifyContent: 'center',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    width: `calc(100% - ${50}px)`,
  }),
}));

export const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: '72px !important',
  padding: '0 !important',
  position: 'relative',
});

// ============================================
// Menu Toggle Button
// ============================================
export const MenuToggleButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ isOpen }) => ({
  position: 'absolute',
  left: isOpen ? '-60px' : '-61px',
  top: '113%',
  transform: 'translateY(-50%)',
  width: '54px',
  height: '54px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: '50%',
  transition: 'all 0.65s',
  '& svg': {
    transition: 'transform 0.4s',
  },
}));

// ============================================
// Sidebar Guide Tooltip
// ============================================
export const SidebarGuide = styled(Box, {
  shouldForwardProp: prop => prop !== 'isOpen',
})(({ isOpen }) => ({
  position: 'absolute',
  left: isOpen ? '4px' : '3px',
  top: '148%',
  transform: 'translateY(-50%)',
  backgroundColor: '#0F7F85',
  color: '#fff',
  borderRadius: '8px',
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  whiteSpace: 'nowrap',
  zIndex: 1300,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  // 안내 말풍선이 아래 화면 버튼(지점 스케줄 '설정' 등)을 덮어 클릭을 막지 않게 한다.
  // 클릭은 아래로 통과해 ClickAwayListener 가 말풍선을 닫고, 닫기(✕)만 직접 받는다.
  pointerEvents: 'none',
  animation: 'sidebarGuideFadeIn 0.3s ease-out',
  '@keyframes sidebarGuideFadeIn': {
    from: { opacity: 0, transform: 'translateY(-50%) translateX(-8px)' },
    to: { opacity: 1, transform: 'translateY(-50%) translateX(0)' },
  },
}));

export const SidebarGuideArrow = styled(Box)({
  position: 'absolute',
  left: '-6px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 0,
  height: 0,
  borderTop: '6px solid transparent',
  borderBottom: '6px solid transparent',
  borderRight: '6px solid #0F7F85',
});

export const SidebarGuideText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#fff',
});

export const SidebarGuideClose = styled(Box)({
  fontSize: '12px',
  cursor: 'pointer',
  color: 'rgba(255,255,255,0.7)',
  flexShrink: 0,
  pointerEvents: 'auto',
  '&:hover': {
    color: '#fff',
  },
});

// ============================================
// Left Section (Title + Breadcrumb + Branch Select)
// ============================================
export const LeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

export const TitleWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  // 헤더가 좁아지면(예: 통합관리자 지점 선택 2개, 화면 폭 ~1500px) 제목·경로가 한 글자씩 세로로 꺾여 헤더 아래로
  // 늘어나고, 그 투명한 영역이 본문 상단 버튼(지점 스케줄 [설정])을 덮어 클릭을 가로챘다.
  // 헤더 높이 안으로 자르고 클릭은 통과시킨다(제목·경로에 링크 없음). 넓은 화면의 모양은 그대로.
  maxHeight: '64px',
  overflow: 'hidden',
  pointerEvents: 'none',
});

export const PageTitle = styled(Typography)({
  fontSize: '24px',
  fontWeight: 700,
  color: '#262626',
  lineHeight: 1,
});

export const BreadcrumbWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const BreadcrumbText = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
  color: '#a6a6a6',
  fontSize: '16px',
  fontWeight: 500,
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: '#a6a6a6',
  },
});

export const OperatorsText = styled(Typography)({
  fontSize: '28px',
  fontWeight: '700',
  color: '#000',
  position: 'fixed',
  top: '40px',
});

// ============================================
// Branch Select (SUPERADMIN)
// ============================================
export const HighlightBorderWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'showAnimation',
})(({ showAnimation }) => ({
  position: 'relative',
  borderRadius: '8px',
  ...(showAnimation && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0F7F85 !important',
      borderWidth: '2px !important',
    },
    animation: `${pulseGlow} 1.5s ease-in-out infinite`,
  }),
}));

export const SelectWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const SelectLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#595959',
});

export const BranchFormControl = styled(FormControl)({
  position: 'relative',
  zIndex: 1,
});

export const SelectGroup = styled(Box)({
  display: 'flex',
  gap: '12px',
});

export const PlaceholderText = styled('span')({
  color: '#a6a6a6',
  fontSize: '16px',
});

export const BranchSelect = styled(Select)({
  height: '48px',
  minWidth: '280px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  color: '#262626',
  backgroundColor: '#fff',
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
    borderWidth: '2px',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#4AB3BC',
    borderWidth: '2px',
  },
  '& .MuiSelect-select': {
    padding: '12px 16px',
  },
  '& .MuiSelect-icon': {
    color: '#262626',
    right: '16px',
  },
});

export const PlaceholderMenuItem = styled(MenuItem)({
  fontSize: '16px',
  color: '#a6a6a6',
});

export const BranchMenuItem = styled(MenuItem)({
  fontSize: '16px',
  minHeight: '48px',
});

export const ChosungDivider = styled('li')({
  padding: '6px 16px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#0F7F85',
  backgroundColor: '#F8F8F8',
  borderTop: '1px solid #EBEBEB',
  listStyle: 'none',
  cursor: 'default',
  userSelect: 'none',
});

export const CenterSearchInput = styled(TextField)({
  margin: '8px 12px',
  '& .MuiOutlinedInput-root': {
    height: '36px',
    fontSize: '14px',
    borderRadius: '6px',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4AB3BC',
      borderWidth: '1.5px',
    },
  },
});

// ============================================
// Right Section (Search + Register + User)
// ============================================
export const RightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
});

export const SearchFieldWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '48px',
  border: '1px solid #b3b3b3',
  borderRadius: '999px',
  paddingLeft: '24px',
  paddingRight: '24px',
  backgroundColor: '#fff',
  // 자식 input이 포커스되면 wrapper의 border 색을 teal로
  '&:focus-within': {
    borderColor: '#4AB3BC',
    borderWidth: '2px',
  },
});

export const StyledAutocomplete = styled(Autocomplete)({
  width: '250px',
  '& .MuiAutocomplete-popupIndicator': {
    transform: 'none',
  },
  '& .MuiAutocomplete-popupIndicatorOpen': {
    transform: 'none',
  },
  // 결과 행이 [이름 / 전화 / 뱃지] 3요소라 입력창 폭(250px)으로는 좁아 팝업만 넓힌다.
  // disablePortal 이라 popper 는 형제 요소이고, 폭은 MUI 가 인라인으로 넣으므로 !important 필요.
  '& + .MuiAutocomplete-popper': {
    width: '340px !important',
  },
  '& + .MuiAutocomplete-popper .MuiAutocomplete-listbox': {
    padding: 0,
    maxHeight: '360px',
  },
  '& + .MuiAutocomplete-popper .MuiAutocomplete-option, & .MuiAutocomplete-option': {
    padding: 0,
    borderBottom: '1px solid #f5f5f5',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  '& + .MuiAutocomplete-popper .MuiAutocomplete-noOptions, & .MuiAutocomplete-noOptions': {
    fontSize: '13px',
    color: '#999',
    textAlign: 'center',
  },
});

// 검색 결과 행 — 판매등록 수강권검색 리스트 행과 같은 규격
export const SearchOptionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
  padding: '10px 14px',
});

export const SearchOptionName = styled('span')({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const SearchOptionPhone = styled('span')({
  fontSize: '12px',
  color: '#999',
  flexShrink: 0,
});

export const SearchTypeBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'isTemporary',
})(({ isTemporary }) => ({
  fontSize: '10px',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: '3px',
  flexShrink: 0,
  color: isTemporary ? '#4b5563' : '#4ab3bc',
  backgroundColor: isTemporary ? '#EEF0F3' : '#edf8f9',
  border: `1px solid ${isTemporary ? '#dfe3e8' : '#d0edf0'}`,
}));

export const SearchPopupIcon = styled(SearchIcon)({
  color: '#bfbfbf',
  fontSize: '24px',
});

export const SearchTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    padding: '0 !important',
    height: '35px',
    display: 'flex',
    alignItems: 'center',
    '& fieldset': {
      border: 'none',
    },
  },
  // Autocomplete의 .MuiAutocomplete-input 이 .MuiInputBase-input 을 덮어쓰므로 둘 다 명시
  '& .MuiInputBase-input, & .MuiAutocomplete-input': {
    fontSize: '16px !important',
    fontWeight: 500,
    color: '#b3b3b3',
    padding: '0 !important',
    '&::placeholder': {
      color: '#b3b3b3',
      opacity: 1,
      fontSize: '16px !important',
    },
  },
});

// ============================================
// Register Button
// ============================================
export const RegisterButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isDisabled',
})(({ isDisabled }) => ({
  borderColor: isDisabled ? '#a6a6a6' : '#4AB3BC',
  color: isDisabled ? '#a6a6a6' : '#4AB3BC',
  borderRadius: '999px',
  padding: '9px 16px 9px 20px',
  fontSize: '16px',
  fontWeight: 500,
  textTransform: 'none',
  // 창이 좁아지면 '잠재회원 / 등록' 두 줄로 꺾여 버튼이 밀리던 문제 — 한 줄 고정, 줄어들지 않게
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&:hover': {
    borderColor: isDisabled ? '#a6a6a6' : '#4AB3BC',
    backgroundColor: isDisabled ? 'transparent' : 'rgba(15, 127, 133, 0.04)',
  },
  '&.Mui-disabled': {
    borderColor: '#a6a6a6',
    color: '#a6a6a6',
  },
}));

// ============================================
// User Section
// ============================================
export const UserSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const UserName = styled(Typography)({
  fontSize: '15px',
  fontWeight: 500,
  color: '#262626',
  cursor: 'pointer',
});

export const UserAvatar = styled(Avatar)({
  width: 40,
  height: 40,
  backgroundColor: '#4AB3BC',
  cursor: 'pointer',
});

// ============================================
// Dropdown Menu
// ============================================
export const StyledMenu = styled(Menu)({
  borderRadius: '8px',
  marginTop: 16,
});

export const LogoutMenuItem = styled(MenuItem)({
  '&:hover': {
    background: 'transparent',
  },
});

// ============================================
// Modal
// ============================================
export const RegisterModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const ModalContent = styled(Box)({
  width: '880px',
  maxWidth: 'calc(100vw - 64px)',
  maxHeight: '90vh',
  overflowY: 'auto',
  outline: 'none',
});

// ============================================
// Breadcrumb Icon
// ============================================
export const BreadcrumbIcon = styled(NavigateNextIcon)({
  marginLeft: '10px',
  marginRight: '8px',
});

// ============================================
// Tooltip Styles (for componentsProps)
// ============================================
export const tooltipStyles = {
  tooltip: {
    sx: {
      fontSize: '14px',
      fontWeight: 500,
      padding: '10px 16px',
      backgroundColor: '#333',
      borderRadius: '8px',
    },
  },
  arrow: {
    sx: {
      color: '#333',
    },
  },
};

// ============================================
// Menu Paper Props
// ============================================
export const menuPaperProps = {
  sx: {
    borderRadius: '12px',
    width: '135px',
    boxShadow: '0 0 20px #33333326',
    padding: '8px',
    ml: -3,
  },
};

export const menuListProps = {
  'aria-labelledby': 'basic-button',
  sx: { color: '#707070' },
};
